import { FormulaEvaluator } from './formulaEvaluator.js';

const DEFAULT_RULE_LABEL = 'Aantalkorting';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Normalize the staffelbasis of a rule.
 *
 * - `{ type: 'quantity' }` (default): tiers match on het aantal stuks.
 * - `{ type: 'field', fieldId }`: tiers match op de som van een numerieke
 *   optie of berekend veld over alle cart items met hetzelfde product
 *   (bijv. totale oppervlakte in m²).
 */
const normalizeBasis = (rule = {}) => {
  const basis = rule.basis || rule.quantitySource || null;
  if (!basis || basis.type !== 'field' || !basis.fieldId) {
    return { type: 'quantity', fieldId: null, fieldLabel: '', unit: '' };
  }

  return {
    type: 'field',
    fieldId: String(basis.fieldId),
    fieldLabel: basis.fieldLabel || basis.field_label || '',
    unit: basis.unit || '',
  };
};

export const normalizeVolumePricingRules = (rules = [], forcedScope = null) => {
  if (!Array.isArray(rules)) return [];

  return rules
    .filter((rule) => rule && rule.enabled !== false)
    .map((rule) => {
      const basis = normalizeBasis(rule);
      // Metric-based staffels (bijv. m²) mogen bij 0 beginnen en decimalen
      // bevatten; aantal-staffels blijven op minimaal 1 stuk geklemd.
      const minTierFloor = basis.type === 'field' ? 0 : 1;

      const fallbackTier = {
        id: `${rule.id || 'rule'}-tier-default`,
        minQuantity: Math.max(minTierFloor, toNumber(rule.minQuantity, minTierFloor)),
        maxQuantity: rule.maxQuantity === null || rule.maxQuantity === ''
          ? null
          : toNumber(rule.maxQuantity, null),
        action: {
          type: rule.action?.type || rule.type || 'discount',
          value: toNumber(rule.action?.value ?? rule.value, 0),
          expression: rule.action?.expression || rule.expression || '',
        },
      };

      const tiers = Array.isArray(rule.tiers) && rule.tiers.length
        ? rule.tiers.map((tier, index) => ({
            id: tier.id || `${rule.id || 'rule'}-tier-${index}`,
            minQuantity: Math.max(minTierFloor, toNumber(tier.minQuantity, minTierFloor)),
            maxQuantity: tier.maxQuantity === null || tier.maxQuantity === ''
              ? null
              : toNumber(tier.maxQuantity, null),
            action: {
              type: tier.action?.type || tier.type || 'discount',
              value: toNumber(tier.action?.value ?? tier.value, 0),
              expression: tier.action?.expression || tier.expression || '',
            },
          }))
        : [fallbackTier];

      return {
        id: rule.id || `volume-rule-${Math.random().toString(36).slice(2)}`,
        label: rule.label || DEFAULT_RULE_LABEL,
        enabled: rule.enabled !== false,
        showInQuote: rule.showInQuote !== false,
        scope: forcedScope || rule.scope || 'same_product',
        groupId: rule.groupId || rule.categoryId || null,
        groupName: rule.groupName || rule.categoryName || '',
        basis,
        tiers,
      };
    });
};

const getProductGroupKeys = (product = {}) => {
  const keys = new Set();

  (product.categoryIds || product.category_ids || []).forEach((id) => keys.add(String(id)));
  (product.categories || []).forEach((category) => {
    if (category?.id) keys.add(String(category.id));
    if (category?.name) keys.add(String(category.name));
    if (category?.slug) keys.add(String(category.slug));
  });

  if (product.category) keys.add(String(product.category));

  return keys;
};

/**
 * Resolve the value of a metric field (numerieke optie of berekend veld) for
 * a single cart item. Voorkeursbron is `item.metrics` (door de cart store
 * vooraf geëvalueerd, inclusief computed fields); fallback is de ruwe
 * optiewaarde uit `item.configuration`.
 */
export const getItemMetricValue = (item, fieldId) => {
  if (!fieldId) return 0;

  const metrics = item?.metrics;
  if (metrics && metrics[fieldId] !== undefined && metrics[fieldId] !== null) {
    return toNumber(metrics[fieldId], 0);
  }

  const productId = item?.product?.id;
  const configuration = item?.configuration || {};
  const directValue = configuration[`${productId}-${fieldId}-input`] ?? configuration[fieldId];
  return toNumber(directValue, 0);
};

export const getCartQuantityContext = (cartItems = []) => {
  const productQuantities = {};
  const productSubtotals = {};
  const productMetrics = {};
  let totalQuantity = 0;
  let cartSubtotal = 0;

  cartItems.forEach((item) => {
    const productId = item?.product?.id;
    const quantity = Math.max(1, toNumber(item?.quantity, 1));
    const unitTotal = toNumber(item?.pricing?.totalPrice, 0);
    const lineTotal = unitTotal * quantity;

    totalQuantity += quantity;
    cartSubtotal += lineTotal;

    if (productId) {
      productQuantities[productId] = (productQuantities[productId] || 0) + quantity;
      productSubtotals[productId] = (productSubtotals[productId] || 0) + lineTotal;

      const metrics = item?.metrics;
      if (metrics && typeof metrics === 'object') {
        if (!productMetrics[productId]) productMetrics[productId] = {};
        Object.entries(metrics).forEach(([fieldId, value]) => {
          productMetrics[productId][fieldId] =
            (productMetrics[productId][fieldId] || 0) + toNumber(value, 0) * quantity;
        });
      }
    }
  });

  return {
    totalQuantity,
    cartSubtotal,
    productQuantities,
    productSubtotals,
    productMetrics,
  };
};

/**
 * Total metric value voor een product over alle cart items (× quantity).
 * Valt terug op per-item resolutie als het veld niet in de vooraf
 * geaggregeerde `productMetrics` zit (bijv. legacy sessionStorage items).
 */
const getProductMetricTotal = (context, cartItems, productId, fieldId) => {
  const aggregated = context.productMetrics?.[productId]?.[fieldId];
  if (aggregated !== undefined) return aggregated;

  return cartItems
    .filter((item) => item?.product?.id === productId)
    .reduce((sum, item) => {
      const quantity = Math.max(1, toNumber(item?.quantity, 1));
      return sum + getItemMetricValue(item, fieldId) * quantity;
    }, 0);
};

const findMatchingTier = (rule, quantity) => {
  return [...(rule.tiers || [])]
    .sort((a, b) => (b.minQuantity || 0) - (a.minQuantity || 0))
    .find((tier) => {
      const min = toNumber(tier.minQuantity, 1);
      const max = tier.maxQuantity === null || tier.maxQuantity === undefined
        ? null
        : toNumber(tier.maxQuantity, null);
      return quantity >= min && (max === null || quantity <= max);
    });
};

const evaluateFormula = (expression, context) => {
  if (!expression) return 0;

  let parsedExpression = expression;
  Object.entries(context).forEach(([key, value]) => {
    parsedExpression = parsedExpression.replace(
      new RegExp(`\\{${key}\\}`, 'g'),
      toNumber(value, 0).toString()
    );
  });

  if (!/^[0-9+\-*/.() ]+$/.test(parsedExpression)) {
    console.warn('Invalid volume pricing formula:', expression);
    return 0;
  }

  try {
    const result = Function(`'use strict'; return (${parsedExpression})`)();
    return Number.isFinite(Number(result)) ? Number(result) : 0;
  } catch (error) {
    console.warn('Failed to evaluate volume pricing formula:', error);
    return 0;
  }
};

const applyAction = (rule, basis, context) => {
  const action = rule.action || {};
  const value = toNumber(action.value, 0);

  switch (action.type) {
    case 'add':
      return value;
    case 'subtract':
      return -value;
    case 'discount':
      return basis * (value / 100) * -1;
    case 'multiply':
      return basis * (value || 1) - basis;
    case 'override':
      return value - basis;
    case 'formula':
      return evaluateFormula(action.expression, context);
    default:
      return 0;
  }
};

export const calculateVolumePricing = (cartItems = [], cartRules = []) => {
  const context = getCartQuantityContext(cartItems);
  const adjustments = [];
  const processedProductIds = new Set();

  cartItems.forEach((item) => {
    const product = item?.product || {};
    const productId = product.id;
    if (!productId || processedProductIds.has(productId)) return;
    processedProductIds.add(productId);

    const productRules = normalizeVolumePricingRules(product.volumePricingRules, 'same_product');
    const productQuantity = context.productQuantities[productId] || 0;

    productRules.forEach((rule) => {
      const usesMetricBasis = rule.basis?.type === 'field' && rule.basis.fieldId;
      const metricTotal = usesMetricBasis
        ? getProductMetricTotal(context, cartItems, productId, rule.basis.fieldId)
        : 0;
      const tierQuantity = usesMetricBasis ? metricTotal : productQuantity;

      const tier = findMatchingTier(rule, tierQuantity);
      if (!tier) return;

      const basis = context.productSubtotals[productId] || 0;
      const value = applyAction({ action: tier.action }, basis, {
        same_product_quantity: productQuantity,
        same_product_metric: metricTotal,
        cart_total_quantity: context.totalQuantity,
        subtotal: basis,
        total: basis,
      });

      if (!value) return;

      const displaySuffix = usesMetricBasis
        ? `(${Math.round(metricTotal * 100) / 100}${rule.basis.unit ? ` ${rule.basis.unit}` : ''})`
        : `(${productQuantity}x)`;

      adjustments.push({
        id: `${productId}-${rule.id}`,
        ruleId: rule.id,
        tierId: tier.id,
        scope: 'same_product',
        productId,
        productName: product.name || product.title || 'Product',
        label: rule.label || DEFAULT_RULE_LABEL,
        displayValue: `${rule.label || DEFAULT_RULE_LABEL} ${displaySuffix}`,
        value,
        price: value,
        quantity: 1,
        basis: rule.basis,
        metricTotal: usesMetricBasis ? metricTotal : null,
        showOnQuote: rule.showInQuote !== false,
        pricesIncludeVat: product.pricesIncludeVat ?? null,
        vatRate: product.vatRate ?? 21,
        rule,
        tier,
      });
    });
  });

  const normalizedCartRules = normalizeVolumePricingRules(cartRules).sort((a, b) => {
    const order = { product_group: 0, cart_total: 1 };
    return (order[a.scope] ?? 99) - (order[b.scope] ?? 99);
  });
  normalizedCartRules.forEach((rule) => {
    let quantity = context.totalQuantity;
    let basis =
      context.cartSubtotal + adjustments.reduce((sum, adjustment) => sum + adjustment.value, 0);
    let matchingProducts = [];

    if (rule.scope === 'product_group') {
      const groupKey = rule.groupId || rule.groupName;
      if (!groupKey) return;

      matchingProducts = cartItems.filter((item) =>
        getProductGroupKeys(item?.product || {}).has(String(groupKey))
      );

      quantity = matchingProducts.reduce((sum, item) => sum + Math.max(1, toNumber(item?.quantity, 1)), 0);
      basis = matchingProducts.reduce((sum, item) => {
        const quantity = Math.max(1, toNumber(item?.quantity, 1));
        const unitTotal = toNumber(item?.pricing?.totalPrice, 0);
        return sum + quantity * unitTotal;
      }, 0);

      const matchingProductIds = new Set(
        matchingProducts.map((item) => item?.product?.id).filter(Boolean)
      );
      basis += adjustments
        .filter(
          (adjustment) =>
            adjustment.scope === 'same_product' && matchingProductIds.has(adjustment.productId)
        )
        .reduce((sum, adjustment) => sum + adjustment.value, 0);
    }

    const tier = findMatchingTier(rule, quantity);
    if (!tier) return;

    const currentTotal =
      context.cartSubtotal + adjustments.reduce((sum, adjustment) => sum + adjustment.value, 0);
    const value = applyAction({ action: tier.action }, basis, {
      same_product_quantity: rule.scope === 'product_group' ? quantity : 0,
      cart_total_quantity: context.totalQuantity,
      subtotal: context.cartSubtotal,
      total: currentTotal,
    });

    if (!value) return;

    adjustments.push({
      id: rule.id,
      ruleId: rule.id,
      tierId: tier.id,
      scope: rule.scope || 'cart_total',
      groupId: rule.groupId || null,
      groupName: rule.groupName || '',
      label: rule.label || DEFAULT_RULE_LABEL,
      displayValue: `${rule.label || DEFAULT_RULE_LABEL} (${quantity}x)`,
      value,
      price: value,
      quantity: 1,
      showOnQuote: rule.showInQuote !== false,
      pricesIncludeVat: null,
      vatRate: 21,
      rule,
      tier,
      matchingProductIds: matchingProducts.map((item) => item?.product?.id).filter(Boolean),
    });
  });

  const adjustmentsTotal = adjustments.reduce((sum, adjustment) => sum + adjustment.value, 0);

  const result = {
    ...context,
    adjustments,
    adjustmentsTotal,
    totalBeforeVolumePricing: context.cartSubtotal,
    totalAfterVolumePricing: context.cartSubtotal + adjustmentsTotal,
  };

  return result;
};

const getAdjustmentTargets = (adjustment, cartItems) => {
  if (adjustment.scope === 'same_product') {
    return cartItems.filter((item) => item?.product?.id === adjustment.productId);
  }

  if (adjustment.scope === 'product_group') {
    const matchingIds = new Set(adjustment.matchingProductIds || []);
    return cartItems.filter((item) => matchingIds.has(item?.product?.id));
  }

  return cartItems;
};

const allocateAdjustment = (adjustment, cartItems) => {
  const targets = getAdjustmentTargets(adjustment, cartItems);
  if (!targets.length) return [];

  const metricFieldId =
    adjustment.scope === 'same_product' && adjustment.basis?.type === 'field'
      ? adjustment.basis.fieldId
      : null;

  const targetLines = targets.map((item) => {
    const quantity = Math.max(1, toNumber(item?.quantity, 1));
    const unitTotal = toNumber(item?.pricing?.totalPrice, 0);
    return {
      item,
      quantity,
      lineTotal: unitTotal * quantity,
      metricTotal: metricFieldId ? getItemMetricValue(item, metricFieldId) * quantity : 0,
    };
  });

  // Field-basis staffels (bijv. m²) are priced on the metric, so split on each
  // line's own metric; otherwise a line with only a priced upsell takes it all.
  const metricWeight = targetLines.reduce((sum, line) => sum + Math.max(0, line.metricTotal), 0);
  const totalWeight = targetLines.reduce((sum, line) => sum + Math.max(0, line.lineTotal), 0);
  const quantityWeight = targetLines.reduce((sum, line) => sum + line.quantity, 0);

  const getWeight = (line) => {
    if (metricWeight > 0) return Math.max(0, line.metricTotal) / metricWeight;
    if (totalWeight > 0) return Math.max(0, line.lineTotal) / totalWeight;
    return line.quantity / quantityWeight;
  };

  let allocated = 0;

  return targetLines.map((line, index) => {
    const isLast = index === targetLines.length - 1;
    const weight = getWeight(line);
    const value = isLast ? adjustment.value - allocated : adjustment.value * weight;
    allocated += value;

    return {
      itemId: line.item.id,
      productId: line.item?.product?.id,
      value,
      adjustment,
    };
  });
};

export const allocateVolumePricingToItems = (cartItems = [], volumePricing = null) => {
  const allocationsByItemId = {};

  (volumePricing?.adjustments || []).forEach((adjustment) => {
    allocateAdjustment(adjustment, cartItems).forEach((allocation) => {
      if (!allocation.itemId) return;
      if (!allocationsByItemId[allocation.itemId]) {
        allocationsByItemId[allocation.itemId] = {
          total: 0,
          adjustments: [],
        };
      }

      allocationsByItemId[allocation.itemId].total += allocation.value;
      allocationsByItemId[allocation.itemId].adjustments.push(allocation);
    });
  });

  return allocationsByItemId;
};

/**
 * Numeric option values plus computed fields (e.g. oppervlakte) for one item.
 * Mirrors the cart store so a single-product configurator can staffel on m².
 */
export function buildItemMetrics(product, configuration = {}) {
  const metrics = {};
  const context = {};

  (product?.options || []).forEach((option) => {
    if (!option?.id) return;
    const fieldId = `${product.id}-${option.id}-input`;
    const value = configuration[fieldId];
    if (value === undefined || value === null || value === '') return;

    context[option.id] = value;
    context[fieldId] = value;
    if (option.type === 'number') {
      metrics[option.id] = toNumber(value, 0);
    }
  });

  const computedFields = product?.computedFields || product?.computed_fields || [];
  if (Array.isArray(computedFields) && computedFields.length) {
    const evaluator = new FormulaEvaluator();
    computedFields.forEach((field) => {
      if (!field?.id || !field?.formula) return;
      const value = Number(evaluator.evaluateFormula(field.formula, context, null)) || 0;
      context[field.id] = value;
      metrics[field.id] = value;
    });
  }

  return metrics;
}

/**
 * Add same-product volume rules on top of a configurator price.
 * Returns the original pricing when the product has no staffel or no tier matches.
 * The cart already bakes this in; callers that submit `volumePricing` must keep
 * doing so, otherwise the backend would apply the staffel a second time.
 */
export function applyProductVolumeAdjustment(product, configuration = {}, pricing = {}) {
  const baseTotal = Number(pricing.totalPrice) || 0;
  const baseProductOnly =
    pricing.productOnlyPrice !== undefined && pricing.productOnlyPrice !== null
      ? Number(pricing.productOnlyPrice) || 0
      : baseTotal;
  const rules = product?.volumePricingRules || product?.volume_pricing_rules || [];

  if (!product?.id || !Array.isArray(rules) || rules.length === 0) {
    return { ...pricing, volumeAdjustment: 0 };
  }

  const item = {
    id: 'configurator-preview',
    product: { ...product, volumePricingRules: rules },
    configuration,
    metrics: buildItemMetrics(product, configuration),
    quantity: 1,
    pricing: {
      totalPrice: baseTotal,
      unitPrice: baseTotal,
      productOnlyPrice: baseProductOnly,
    },
  };
  const volume = calculateVolumePricing([item], []);
  const adjustment = (volume?.adjustments || []).reduce(
    (sum, entry) => sum + (Number(entry.value) || 0),
    0
  );
  if (!adjustment) return { ...pricing, volumeAdjustment: 0 };

  const allocations = allocateVolumePricingToItems([item], volume);
  const itemAllocation = allocations['configurator-preview'] || {
    total: adjustment,
    adjustments: [],
  };

  return {
    ...pricing,
    totalPrice: baseTotal + adjustment,
    productOnlyPrice: baseProductOnly + adjustment,
    volumeAdjustment: adjustment,
    breakdown: [
      ...(pricing.breakdown || []),
      ...itemAllocation.adjustments.map((entry) => ({
        type: 'volume',
        label: entry.adjustment?.label || 'Staffel',
        value: Number(entry.value) || 0,
        price: Number(entry.value) || 0,
        quantity: 1,
        displayValue: entry.adjustment?.displayValue || null,
      })),
    ],
    volumePricing: {
      originalTotalPrice: baseTotal,
      originalProductOnlyPrice: baseProductOnly,
      adjustmentTotal: adjustment,
      adjustmentPerUnit: adjustment,
      allocations: itemAllocation.adjustments,
    },
  };
}
